import { createAction } from 'redux-actions';
import moment from 'moment';
import {
  createParams,
  validateParams,
  getValueFromObjects,
  getCPRMapping,
  getCustomerAge,
  getDefaultOrValueFromObjects,
} from './common';
import Product from './product';
import * as types from '../../constants/Api';
import { createErrorBody } from '../error';

class PregnancyService extends Product {
  static TYPE = 'pregnancy';
  static GET_PRICE = 'pregnancy/v1/price';
  static UPDATE_PRICE = 'pregnancy/v1/price';
  static PREGNANCY_PURCHASE = 'pregnancy/v1/purchase';

  static createdPregnancy = createAction(types.PREGNANCY_CREATED);
  static createPregnancy = createAction(types.PREGNANCY_CREATE);
  static updatedPregnancy = createAction(types.PREGNANCY_UPDATED);
  static updatePregnancy = createAction(types.PREGNANCY_UPDATE);
  static preparedPregnancy = createAction(types.PREGNANCY_PREPARED);
  static preparePregnancy = createAction(types.PREGNANCY_PREPARE);

  constructor() {
    super(
      PregnancyService.TYPE,
      PregnancyService.createPregnancy,
      PregnancyService.updatePregnancy,
      PregnancyService.preparePregnancy
    );
    super.prepareCreateUpdateResponse = this.prepareCreateUpdateResponse;
  }

  createdPregnancyAction(payload) {
    return (dispatch) => dispatch(PregnancyService.createdPregnancy(payload));
  }

  createPregnancyAction(mappedParams, actionOnErrors, reqModel, formValues) {
    return (dispatch, getState) => {
      return dispatch(
        this.requestCreateProductV2(
          PregnancyService.GET_PRICE,
          mappedParams,
          reqModel,
          formValues
        )
      )
        .then((data) => {
          dispatch(this.createdPregnancyAction(data));
        })
        .catch((data) => {
          return this.handleError(actionOnErrors, data, getState, dispatch);
        });
    };
  }

  updatedPregnancyAction(payload) {
    return (dispatch) => dispatch(PregnancyService.updatedPregnancy(payload));
  }

  updatePregnancyAction(mappedParams, actionOnErrors, reqModel, formValues) {
    return (dispatch, getState) => {
      return dispatch(
        this.requestCreateProductV2(
          PregnancyService.UPDATE_PRICE,
          mappedParams,
          reqModel,
          formValues
        )
      )
        .then((data) => {
          dispatch(this.updatedPregnancyAction(data));
        })
        .catch((data) => {
          return this.handleError(actionOnErrors, data, getState, dispatch);
        });
    };
  }

  createUpdatePregnancyAction(params, actionOnErrors, values) {
    return (dispatch, getState) => {
      const state = getState();
      const formValues = { ...values, ...state.userData };
      const mappedParams = params
        ? createParams(params, formValues, state)
        : {};
      const errors = params ? validateParams(mappedParams, params) : [];

      if (errors.length > 0) {
        return Promise.reject(createErrorBody('Missing parameters', errors));
      }
      if (state.userData.age || state.userData.age === null) {
        state.userData.age = values.age;
        formValues.age = state.userData.age;
      }
      const requestModel = {
        customer: {
          age: getCustomerAge(mappedParams, formValues) || 0,
          birthdate: getValueFromObjects(
            'birthdate',
            mappedParams,
            formValues
          ) || '',
          civilRegistrationNumber: getCPRMapping(mappedParams, formValues),
          rolePlayerId: getValueFromObjects(
            'rolePlayerId',
            mappedParams,
            formValues
          ) || '',
        },
        pregnancyInsurance: {
          numberOfUnbornChildren: +getValueFromObjects('numberOfUnbornChildren', mappedParams, formValues) || null,
          expectedDueDate: moment(getValueFromObjects('expectedDueDate', mappedParams, formValues), 'DD-MM-YYYY').format('YYYY-MM-DD') || null,
          firstTimeGivenBirth: getValueFromObjects('firstTimeGivenBirth', mappedParams, formValues),
        },
        packageText: getValueFromObjects(
          'packageText',
          mappedParams,
          formValues
        ),
        coverage: [],
      };
      if (state.userData.pregnancy) {
        return dispatch(
          this.updatePregnancyAction(mappedParams, actionOnErrors, requestModel, {
            ...formValues,
          })
        ).catch(data => {
          return Promise.resolve(data);
        });
      }

      return dispatch(
        this.createPregnancyAction(mappedParams, actionOnErrors, requestModel, formValues)
      ).catch(data => {
        return Promise.resolve(data);
      });
    };
  }

  getExcessAmount(formValues, dispatch) {
    return this.getPriceAuxiliaryValues(formValues, dispatch)
      .then((response) => {
        return response.deductible;
      });
  }

  activationDateGetAction(formValues) {
    return super.activationDateGetAction(
      formValues,
      this.getPregnancyMapping(formValues)
    );
  }

  getActivationDates(formValues) {
    return super.getActivationDates(formValues, this.getPregnancyMapping(formValues));
  }

  getPregnancyMapping(formValues, mappedParams = {}) {
    return {
      product: {
        type: PregnancyService.TYPE,
        pregnancyLocation: getValueFromObjects(
          'pregnancyLocation',
          mappedParams,
          formValues
        ),
        pregnancyDaysCovered: getValueFromObjects(
          'pregnancyDaysCovered-pregnancy',
          mappedParams,
          formValues
        ),
        pregnancyValue: getValueFromObjects('pregnancyValue-pregnancy', mappedParams, formValues),
        totalInsured: getValueFromObjects('totalInsured', mappedParams, formValues),
        totalInsuredChildren: getValueFromObjects('totalInsuredChildren', mappedParams, formValues),
      },
    };
  }

  getChildInsuranceConsent(formValues, mappedParams) {
    return {
      dataExchangeWithPartnerConsent: getDefaultOrValueFromObjects('hasDataExchangeWithPartnerConsent', mappedParams, formValues, false),
      unionMemberShipConsent: getDefaultOrValueFromObjects('hasUnionMemberShipConsent', mappedParams, formValues, false),
      childInsuranceConsent: getDefaultOrValueFromObjects('childInsuranceConsent', mappedParams, formValues, false),
    };
  }

  purchase(params, values) {
    return (dispatch, getState) => {
      const state = getState();
      const formValues = { ...values, ...state.userData };
      const mappedParams = params ? createParams(params, formValues, state) : {};
      const errors = params ? validateParams(mappedParams, params) : [];

      if (errors.length > 0) {
        return Promise.reject(createErrorBody('Missing parameters', errors));
      }

      const requestModel = {
        payment: {
          accountNumber: getValueFromObjects('bankAccountNumber', mappedParams, formValues),
          bankNumber: getValueFromObjects('bankRegistrationNumber', mappedParams, formValues),
        },
        electronicContact: {
          emailAddress: getValueFromObjects('emailAddress', mappedParams, formValues),
          mobilePhoneNumber: getValueFromObjects('telephoneNumber', mappedParams, formValues),
          promotionAccept: getValueFromObjects('perm_contactInfo_accept', mappedParams, formValues) || false,
          SMSPermissionForPromotion: getValueFromObjects('perm_contactInfo_accept', mappedParams, formValues) || false,
        },
        consent: this.getChildInsuranceConsent(formValues, mappedParams),
      };

      return dispatch(super.purchaseV2(PregnancyService.PREGNANCY_PURCHASE,
        mappedParams, requestModel, formValues));
    };
  }

  calculatePriceAndFee(price, feeList, formValues) {
    const priceWithFee = Math.round(price.premiumAmount * 100) / 100;

    return {
      premiumPrice: price.premiumWithOutFee,
      priceWithFee: `${priceWithFee}`,
      insuranceFee: price.insuranceToll,
      paymentFee: formValues.paymentFee || '',
    };
  }

  prepareCreateUpdateResponse(payload, mappedParams, formValues) {
    return {
      sessionKey: payload.generalInsurance.sessionKey,
      paymentFrequency: payload.payment.paymentFrequency,
      paymentMethod: payload.payment.paymentMethod,
      priceApiResponse: [
        {
          offeredPrice: this.calculatePriceAndFee(
            payload.price.offeredPrice,
            payload.price.fees,
            formValues
          ),
          listPrice: this.calculatePriceAndFee(
            payload.price.listPrice,
            payload.price.fees,
            formValues
          ),
          advantagePrice: this.calculatePriceAndFee(
            payload.price.advantagePrice,
            payload.price.fees,
            formValues
          ),
          discountAmount:
            this.calculatePriceAndFee(
              payload.price.listPrice,
              payload.price.fees,
              formValues
            ).priceWithFee -
            this.calculatePriceAndFee(
              payload.price.offeredPrice,
              payload.price.fees,
              formValues
            ).priceWithFee,
        },
      ],
      totalOfferedPrice: {
        priceWithFee: payload.price.offeredPrice.premiumWithOutFee,
      },
      usedParams: payload.usedParams,
    };
  }
}

export const pregnancyService = new PregnancyService();
